import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useBasket } from './useBasket';
import { getInvoiceSummary, NO_TEAM_DISCOUNTS } from '@bw-ui/basket';
import type { InvoiceSummary, TeamDiscounts } from '@bw-ui/basket';
import type {
  InvoicePreview,
  InvoicePreviewError,
} from '@portal/types/nexudus';
import { queryKeys } from '@portal/ui/hooks/queryKeys';

type InvoiceResult = (InvoicePreview & Partial<InvoicePreviewError>) | null;

export interface UseInvoicePreviewResult {
  summary: InvoiceSummary;
  /** The raw invoice preview from the store, for per-item matching and error inspection */
  invoicePreview: InvoiceResult;
  isLoading: boolean;
  error: string;
  /**
   * Manually trigger a preview fetch. Returns the raw invoice so callers
   * can inspect it (e.g. for discount validation).
   */
  refetch: () => Promise<InvoiceResult>;
  /** True when there are basket items but no preview could be loaded */
  noPreviewAvailable: boolean;
}

const emptySummary: InvoiceSummary = {
  subtotal: 0,
  tax: 0,
  total: 0,
  totalDiscount: 0,
  discountLines: [],
  accountCredits: 0,
  amountDue: 0,
};

/**
 * Manages the invoice preview lifecycle for a basket:
 * - Auto-fetches when the basket size or discount code changes (via queryKey)
 * - Clears when the basket empties
 * - Derives display summary via getInvoiceSummary, predicting the account
 *   credit Nexudus will apply from `accountBalance` (see useAccountBalance)
 * - Exposes refetch() for imperative triggers (discount apply, modal close)
 *
 * IMPORTANT: Components using this hook must be wrapped with MobX `observer()`.
 */
export function useInvoicePreview(
  isLoggedIn: boolean,
  teamDiscounts: TeamDiscounts = NO_TEAM_DISCOUNTS,
  accountBalance = 0,
): UseInvoicePreviewResult {
  const { data, actions } = useBasket();

  const itemCount = data.items.length;

  // Clear the store's preview when the basket empties.
  // This can't be handled by the query since disabled queries don't run.
  useEffect(() => {
    if (itemCount === 0) {
      actions.clearInvoicePreview();
    }
  }, [itemCount, actions]);

  const query = useQuery<InvoiceResult>({
    // Discount code is intentionally excluded from the key — discount changes
    // are handled via manual refetch() in handleApplyDiscount so we don't
    // race an auto-refetch against the manual one.
    queryKey: queryKeys.invoicePreview(itemCount),
    queryFn: async () => {
      const result = await actions.loadInvoicePreview(true);
      return (result as InvoiceResult) ?? null;
    },
    enabled: isLoggedIn && itemCount > 0,
    // Don't cache — the store is the source of truth and basket
    // contents change the result even at the same item count.
    gcTime: 0,
    staleTime: 0,
  });

  const invoicePreview = (data.invoicePreview as InvoiceResult) ?? null;

  const summary = invoicePreview
    ? getInvoiceSummary(invoicePreview, teamDiscounts, { accountBalance })
    : emptySummary;

  const noPreviewAvailable =
    !query.isLoading && !query.isFetching && !invoicePreview && itemCount > 0;

  const refetch = async (): Promise<InvoiceResult> => {
    const queryResult = await query.refetch();
    if (queryResult.isError) return null;
    return queryResult.data ?? null;
  };

  return {
    summary,
    invoicePreview,
    isLoading: query.isLoading || query.isFetching,
    error: query.error
      ? 'Failed to load invoice preview. Please try again.'
      : '',
    refetch,
    noPreviewAvailable,
  };
}
